/**
 * HeroQuickVerify · live verification input embedded in the hero. A visitor
 * types a DOT or MC number and gets a real FMCSA result without leaving
 * the fold. The full breakdown lives in TryItNowSection below — this is
 * the no-friction proof that the product actually works.
 *
 * States:
 *   idle    · compact input + Verify button, sample hint underneath
 *   loading · single-line skeleton with the scan label
 *   result  · trimmed result card (name, identifiers, trust pill, deep-link)
 *   error   · inline error sentence under the input
 *
 * Reuses the same `carrier-lookup-public` edge function as TryItNowSection
 * so behavior stays consistent across the page. Result rendering is
 * intentionally minimal · the full version with risk flags + bond/insurance
 * pills is one scroll down.
 */

import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ShieldCheck, AlertTriangle, ShieldAlert, Loader2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { FunctionsHttpError } from "@supabase/supabase-js";
import { statusTone, BADGE_TONE_CLASS } from "@/lib/status-tone";
import { withTimeout } from "@/lib/edge";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { ScoreExplainer } from "@/components/verify/ScoreExplainer";
import { useAnime } from "@/lib/anime/useAnime";
import { EASE } from "@/lib/anime/motion-vocab";
import { track } from "@/lib/analytics";
import { detectCanadianIdentifier } from "@/lib/carrier-identifier";
import { Sentry } from "@/lib/sentry";

const FLAG_LABELS: Record<string, string> = {
  recently_registered: "Recently registered (< 90 days)",
  no_insurance: "No insurance on file",
  out_of_service: "Out of service",
  not_authorized: "Not authorized to operate",
  safety_concern: "Safety rating concern",
  small_fleet: "Small fleet (< 3 units)",
  no_surety_bond: "No surety bond on file",
  no_broker_authority: "No active broker authority",
  authority_too_new: "Authority less than 90 days old",
  revoked_authority: "Broker authority revoked",
  multiple_flags: "Multiple risk indicators present",
};

function riskLevelColor(level: string) {
  switch (level) {
    case "LOW": return "text-emerald-400";
    case "MEDIUM": return "text-amber-400";
    case "HIGH": return "text-orange-400";
    case "CRITICAL": return "text-red-400";
    default: return "text-muted-foreground";
  }
}

interface CarrierData {
  legal_name: string;
  dot_number: string;
  mc_number: string;
  operating_status: string;
}

interface RiskAssessment {
  score: number;
  level: "LOW" | "MEDIUM" | "HIGH" | "CRITICAL";
  flags: string[];
  recommendation: "PROCEED" | "CAUTION" | "BLOCK";
}

interface CarrierLookupResult {
  carrier: CarrierData;
  risk_assessment: RiskAssessment;
}

function recBadge(rec: string) {
  switch (rec) {
    case "PROCEED":
      return { label: "Proceed", Icon: ShieldCheck, color: "text-emerald-400", bg: "bg-emerald-500/10", border: "border-emerald-500/30" };
    case "CAUTION":
      return { label: "Caution", Icon: AlertTriangle, color: "text-amber-400", bg: "bg-amber-500/10", border: "border-amber-500/30" };
    case "BLOCK":
      return { label: "Block", Icon: ShieldAlert, color: "text-red-400", bg: "bg-red-500/10", border: "border-red-500/30" };
    default:
      return { label: "Unknown", Icon: ShieldCheck, color: "text-muted-foreground", bg: "bg-muted/10", border: "border-border" };
  }
}

function scrollToFullVerify() {
  const el =
    document.getElementById("try-it-now") ||
    document.querySelector('[data-section="try-it-now"]');
  if (el && "scrollIntoView" in el) {
    (el as HTMLElement).scrollIntoView({ behavior: "smooth", block: "start" });
  }
}

export function HeroQuickVerify() {
  const [identifier, setIdentifier] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<CarrierLookupResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [canadianPrompt, setCanadianPrompt] = useState<{ kind: "CVOR" | "CTQ" | "NSC" } | null>(null);
  const [reportOpen, setReportOpen] = useState(false);
  const resultRef = useRef<HTMLDivElement>(null);
  const canadian = detectCanadianIdentifier(identifier);

  const { ref } = useAnime<HTMLDivElement>({
    opacity: [0, 1],
    translateY: [24, 0],
    scale: [0.97, 1],
    duration: 700,
    delay: 900,
    ease: EASE.soft,
    autoPlay: true,
  });

  useEffect(() => {
    if (result && resultRef.current) {
      resultRef.current.focus();
    }
  }, [result]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const ca = detectCanadianIdentifier(identifier);
    if (ca.isCanadian && ca.kind) {
      setCanadianPrompt({ kind: ca.kind });
      setError(null);
      setResult(null);
      track("hero_quick_verify_ca_detected", { kind: ca.kind });
      return;
    }
    // Preserve any MC/DOT prefix the user typed (or that we filled in via the
    // hint chips below). The edge function classifies based on the prefix —
    // stripping it would force every bare-digit lookup down the DOT path,
    // which silently turns "MC-131029" into "DOT 131029" (a different,
    // possibly non-existent carrier).
    const trimmed = identifier.trim().toUpperCase().replace(/\s+/g, "");
    // Need at least one digit OR letter after dash so "MC-" / "DOT-" / "CAB-"
    // alone don't fire useless lookups with empty payloads.
    if (!/[A-Z0-9]/.test(trimmed.replace(/^[A-Z]+-/, ""))) return;

    // CAB-XX-XXXXXXX → Canadian broker. Route to ca-broker-verify instead of
    // the US carrier-lookup-public path. Format: CAB-<2-letter province>-<alphanumeric id>.
    const isCabId = /^CAB-[A-Z]{2}-[A-Z0-9]+$/i.test(trimmed);

    setLoading(true);
    setError(null);
    setResult(null);
    setCanadianPrompt(null);
    track("hero_quick_verify_submitted", { identifier_length: trimmed.length, is_cab: isCabId });

    try {
      if (isCabId) {
        // CAB ID → Canadian broker registry lookup. FMCSA plays no role here.
        const { data, error: fnError } = await withTimeout(
          supabase.functions.invoke("ca-broker-verify", {
            body: { cab_id: trimmed },
          }),
          20_000,
          "Canadian broker registry is slow to respond. Try again.",
        );

        if (fnError) {
          let code: string | null = null;
          let detail: string | null = null;
          if (fnError instanceof FunctionsHttpError && fnError.context?.response) {
            try {
              const body = await fnError.context.response.clone().json();
              if (typeof body?.error === "string") code = body.error;
              else if (typeof body?.code === "string") code = body.code;
              if (typeof body?.message === "string") detail = body.message;
            } catch {
              // body wasn't JSON — fall through
            }
          }
          const msg = fnError.message ?? "";
          if (code === "rate_limited") {
            setError("Free lookup limit reached (10/hour). Sign up for higher limits.");
          } else if (code === "not_found" || msg.includes("404") || msg.includes("not_found")) {
            setError("No Canadian broker found with that CAB ID. Double-check and try again.");
          } else if (code === "ca_lookup_failed" || msg.includes("502") || msg.includes("unavailable")) {
            // Not FMCSA — this is the CRA / provincial registry path.
            setError("Couldn't reach the registry. Try again in a moment.");
          } else if (code === "invalid_identifier") {
            setError("That doesn't look like a valid CAB ID (format: CAB-ON-XXXXXXX).");
          } else {
            setError(detail ?? "Something went wrong. Please try again.");
          }
          track("hero_quick_verify_failed", { reason: code ?? "edge_error", is_cab: true });
        } else if (data?.broker) {
          // Map CAB broker shape into the CarrierLookupResult the result card
          // expects. The hero card only shows name + identifiers + trust pill,
          // so only legal_name, mc_number (repurposed for CAB ID), and
          // operating_status are load-bearing here.
          const b = data.broker as Record<string, unknown>;
          setResult({
            carrier: {
              legal_name: String(b.legal_name ?? ""),
              dot_number: "",
              mc_number: String(b.cab_id ?? trimmed),
              operating_status: String(b.verification_band ?? b.status ?? "UNKNOWN").toUpperCase(),
            },
            risk_assessment: data.risk_assessment as RiskAssessment,
          });
          track("hero_quick_verify_succeeded", {
            recommendation: data.risk_assessment?.recommendation,
            is_cab: true,
          });
        } else {
          setError("No Canadian broker found with that CAB ID. Double-check and try again.");
          track("hero_quick_verify_failed", { reason: "not_found", is_cab: true });
        }
      } else {
      const { data, error: fnError } = await withTimeout(
        supabase.functions.invoke("carrier-lookup-public", {
          body: { identifier: trimmed, country: "US" },
        }),
        20_000,
        "FMCSA is slow to respond. Try again.",
      );

      if (fnError) {
        // Try to read the structured error body before falling back to the
        // generic message. Mirrors the unwrap pattern in TryItNowSection so
        // codes like signup_required / not_found / fmcsa_unavailable get
        // turned into accurate copy instead of "Something went wrong."
        let code: string | null = null;
        let detail: string | null = null;
        if (fnError instanceof FunctionsHttpError && fnError.context?.response) {
          try {
            const body = await fnError.context.response.clone().json();
            if (typeof body?.error === "string") code = body.error;
            else if (typeof body?.code === "string") code = body.code;
            if (typeof body?.message === "string") detail = body.message;
          } catch {
            // body wasn't JSON — fall through to legacy string match
          }
        }
        const msg = fnError.message ?? "";
        if (code === "rate_limited") {
          setError("Free lookup limit reached (10/hour). Sign up for higher limits.");
        } else if (code === "not_found" || msg.includes("404") || msg.includes("not_found")) {
          setError("No carrier found. Double-check and try again.");
        } else if (
          code === "fmcsa_unavailable" ||
          code === "fmcsa_error" ||
          msg.includes("502") ||
          msg.includes("unavailable")
        ) {
          setError("FMCSA temporarily unavailable.");
        } else if (code === "invalid_identifier") {
          setError("That doesn't look like a valid DOT or MC number.");
        } else {
          setError(detail ?? "Something went wrong. Please try again.");
        }
        track("hero_quick_verify_failed", { reason: code ?? "edge_error" });
      } else if (data?.carrier) {
        setResult({
          carrier: data.carrier as CarrierData,
          risk_assessment: data.risk_assessment as RiskAssessment,
        });
        track("hero_quick_verify_succeeded", {
          recommendation: data.risk_assessment?.recommendation,
        });
      } else {
        setError("No carrier found. Double-check and try again.");
        track("hero_quick_verify_failed", { reason: "not_found" });
      }
      } // end else (non-CAB US path)
    } catch (err: unknown) {
      Sentry.captureException(err, { tags: { surface: "hero_quick_verify" } });
      setError("Connection error. Please try again.");
      track("hero_quick_verify_failed", { reason: "exception" });
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setResult(null);
    setError(null);
    setCanadianPrompt(null);
    setIdentifier("");
  };

  if (canadianPrompt) {
    const kindLabel =
      canadianPrompt.kind === "CVOR" ? "CVOR (Ontario)" :
      canadianPrompt.kind === "CTQ" ? "CTQ/NEQ (Quebec)" :
      "NSC (federal)";
    return (
      <div
        ref={ref}
        style={{ opacity: 0 }}
        className="mt-10 sm:mt-12 mx-auto block w-full max-w-md text-left rounded-2xl border border-white/[0.08] bg-[hsl(0_0%_4%/0.7)] backdrop-blur-xl px-5 py-4 shadow-[0_30px_80px_-20px_hsl(160_100%_45%/0.18)]"
      >
        <p className="text-[10px] font-mono uppercase tracking-widest text-primary mb-1">
          Canadian carrier detected
        </p>
        <p className="text-sm font-semibold text-foreground">
          {kindLabel} lookups need a free account
        </p>
        <p className="text-[12px] text-muted-foreground mt-1.5 leading-relaxed">
          Anonymous demo covers FMCSA only. Sign up free to verify CVOR, CTQ, NSC, plus federal Canadian operating authority.
        </p>
        <div className="flex items-center justify-between gap-2 pt-3 mt-3 border-t border-white/[0.05]">
          <button
            type="button"
            onClick={handleReset}
            className="text-[11px] text-muted-foreground hover:text-foreground transition-colors"
          >
            Try a US identifier
          </button>
          <Link
            to="/signup"
            className="inline-flex items-center gap-1 text-[12px] text-primary font-semibold hover:translate-x-0.5 transition-transform"
          >
            Sign up free <ArrowRight className="h-3 w-3" />
          </Link>
        </div>
      </div>
    );
  }

  if (result) {
    const badge = recBadge(result.risk_assessment.recommendation);
    return (
      <div
        ref={(node) => {
          (resultRef as React.MutableRefObject<HTMLDivElement | null>).current = node;
          (ref as React.MutableRefObject<HTMLDivElement | null>).current = node;
        }}
        tabIndex={-1}
        aria-live="polite"
        style={{ opacity: 0 }}
        className="mt-10 sm:mt-12 mx-auto block w-full max-w-md text-left rounded-2xl border border-white/[0.08] bg-[hsl(0_0%_4%/0.7)] backdrop-blur-xl px-5 py-4 shadow-[0_30px_80px_-20px_hsl(160_100%_45%/0.18)] outline-none animate-in fade-in duration-300"
      >
        <div className="flex items-start justify-between gap-3 mb-3">
          <div className="min-w-0">
            <p className="text-[10px] font-mono uppercase tracking-widest text-primary mb-1">
              Live FMCSA result
            </p>
            <p className="text-sm font-semibold text-foreground truncate">
              {result.carrier.legal_name || "Unknown carrier"}
            </p>
            <p className="text-[11px] text-muted-foreground font-mono mt-0.5">
              MC {result.carrier.mc_number || "—"} · DOT {result.carrier.dot_number || "—"}
            </p>
          </div>
          <span
            className={`shrink-0 inline-flex items-center gap-1 rounded-full ${badge.bg} ${badge.border} border px-2.5 py-1 text-[11px] font-semibold ${badge.color}`}
          >
            <badge.Icon className="h-3 w-3" />
            Trust {result.risk_assessment.score} · {badge.label}
          </span>
        </div>

        <div className="flex items-center justify-between text-[11px] pt-3 border-t border-white/[0.05]">
          <button
            type="button"
            onClick={handleReset}
            className="text-muted-foreground hover:text-foreground transition-colors"
          >
            Verify another
          </button>
          <button
            type="button"
            onClick={() => {
              setReportOpen(true);
              track("hero_quick_verify_full_report_opened", {
                recommendation: result.risk_assessment.recommendation,
              });
            }}
            className="inline-flex items-center gap-1 text-primary font-semibold hover:translate-x-0.5 transition-transform"
          >
            See full report <ArrowRight className="h-3 w-3" />
          </button>
        </div>

        <Dialog open={reportOpen} onOpenChange={setReportOpen}>
          <DialogContent className="max-w-2xl bg-[hsl(0_0%_4%/0.95)] backdrop-blur-xl border-white/[0.08] p-0 overflow-hidden">
            <DialogHeader className="px-6 pt-6 pb-4">
              <DialogTitle className="text-xs font-mono uppercase tracking-widest text-primary font-normal">
                Full verification report
              </DialogTitle>
              <DialogDescription className="sr-only">
                Detailed FMCSA verification, risk assessment, and score breakdown for {result.carrier.legal_name || "this carrier"}.
              </DialogDescription>
            </DialogHeader>

            <div className="flex flex-col sm:flex-row border-t border-white/[0.06]">
              <div className="flex-1 p-6 border-b sm:border-b-0 sm:border-r border-white/[0.06]">
                <p className="text-[10px] font-bold text-muted-foreground uppercase tracking-[0.2em] mb-2">
                  Carrier
                </p>
                <p className="text-lg font-bold text-foreground mb-3 leading-snug">
                  {result.carrier.legal_name || "Unknown"}
                </p>
                <div className="space-y-2 text-sm">
                  <div className="flex items-center gap-2">
                    <span className="text-muted-foreground w-16 shrink-0">DOT</span>
                    <span className="font-mono font-semibold text-foreground">
                      {result.carrier.dot_number || "N/A"}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-muted-foreground w-16 shrink-0">MC</span>
                    <span className="font-mono font-semibold text-foreground">
                      {result.carrier.mc_number || "N/A"}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-muted-foreground w-16 shrink-0">Status</span>
                    <Badge className={BADGE_TONE_CLASS[statusTone(result.carrier.operating_status)]}>
                      {result.carrier.operating_status}
                    </Badge>
                  </div>
                </div>
              </div>

              <div className={`sm:w-56 p-6 flex flex-col items-start sm:items-center justify-center gap-3 ${badge.bg}`}>
                <div className="flex items-center gap-2">
                  <badge.Icon className={`h-6 w-6 ${badge.color}`} />
                  <span className={`text-2xl font-black tracking-tight ${badge.color}`}>
                    {badge.label.toUpperCase()}
                  </span>
                </div>
                <div className="text-center">
                  <p className={`text-4xl font-black font-mono ${badge.color}`}>
                    {result.risk_assessment.score}
                    <span className="text-lg font-semibold text-muted-foreground">/100</span>
                  </p>
                  <p className={`text-xs font-bold uppercase tracking-[0.15em] mt-1 ${riskLevelColor(result.risk_assessment.level)}`}>
                    {result.risk_assessment.level} RISK
                  </p>
                </div>
              </div>
            </div>

            {result.risk_assessment.flags.length > 0 && (
              <div className="px-6 py-4 border-t border-white/[0.06]">
                <p className="text-[10px] font-bold text-muted-foreground uppercase tracking-[0.2em] mb-3">
                  Risk flags
                </p>
                <div className="flex flex-wrap gap-2">
                  {result.risk_assessment.flags.slice(0, 6).map((flag) => (
                    <Badge
                      key={flag}
                      variant="outline"
                      className="border-amber-500/30 text-amber-400 bg-amber-500/5 text-xs"
                    >
                      <AlertTriangle className="h-3 w-3 mr-1 shrink-0" />
                      {FLAG_LABELS[flag] ?? flag}
                    </Badge>
                  ))}
                </div>
              </div>
            )}

            <div className="px-6 py-4 border-t border-white/[0.06]">
              <ScoreExplainer score={result.risk_assessment.score} />
            </div>

            <div className="px-6 py-5 border-t border-primary/10 bg-primary/[0.03]">
              <Link
                to="/signup?role=dispatcher&vertical=freight"
                className="inline-flex items-center gap-1.5 text-sm font-semibold text-primary hover:text-primary/80 transition-colors group"
                onClick={() =>
                  track("hero_quick_verify_upgrade_clicked", {
                    recommendation: result.risk_assessment.recommendation,
                  })
                }
              >
                Sign up to see full trust score history, insurance live-verify, driver-level details, and owner relationships
                <ArrowRight className="h-4 w-4 group-hover:translate-x-0.5 transition-transform" />
              </Link>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  return (
    <div
      ref={ref}
      style={{ opacity: 0 }}
      className="mt-10 sm:mt-12 mx-auto w-full max-w-md"
    >
      <form
        onSubmit={handleSubmit}
        className="rounded-2xl border border-white/[0.08] bg-[hsl(0_0%_4%/0.7)] backdrop-blur-xl px-4 py-3 shadow-[0_30px_80px_-20px_hsl(160_100%_45%/0.18)] flex items-center gap-2"
        aria-label="Quick carrier verification"
      >
        <Input
          type="text"
          aria-label="Carrier identifier"
          placeholder="DOT-1234567, MC-123456, CAB-ON-XXXXXXX, CVOR/CTQ/NSC"
          value={identifier}
          onChange={(e) => setIdentifier(e.target.value)}
          disabled={loading}
          className="bg-transparent border-0 focus-visible:ring-0 focus-visible:ring-offset-0 px-2 h-10 text-sm flex-1 placeholder:text-muted-foreground/60"
        />
        <Button
          type="submit"
          variant="mint"
          size="sm"
          disabled={loading || !identifier.trim()}
          className="h-10 px-4 font-semibold shrink-0"
        >
          {loading ? (
            <span className="inline-flex items-center gap-1.5">
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
              Scanning
            </span>
          ) : (
            "Verify"
          )}
        </Button>
      </form>

      <div className="mt-2 flex items-center justify-between gap-2 px-1">
        <p className="text-[11px] text-muted-foreground/70">
          {error ? (
            <span className="text-destructive">{error}</span>
          ) : (
            <>
              Live FMCSA · try{" "}
              <button
                type="button"
                onClick={() => setIdentifier("DOT-53467")}
                className="font-mono text-primary/80 hover:text-primary underline-offset-2 hover:underline"
                title="Werner Enterprises · DOT 53467 · ACTIVE"
              >
                DOT-53467
              </button>{" "}
              or{" "}
              <button
                type="button"
                onClick={() => setIdentifier("MC-131029")}
                className="font-mono text-primary/80 hover:text-primary underline-offset-2 hover:underline"
                title="C.H. Robinson · MC 131029 · broker"
              >
                MC-131029
              </button>
            </>
          )}
        </p>
        <Link
          to="#try-it-now"
          onClick={(e) => {
            e.preventDefault();
            scrollToFullVerify();
          }}
          className="text-[11px] text-muted-foreground/70 hover:text-foreground transition-colors"
        >
          Broker, CA, more →
        </Link>
      </div>
    </div>
  );
}
